/** A disclosure whose body is **not in the document while it is closed** - ADR-0212 decision 2.
 *
 * A native `<details>` keeps its closed content in the DOM, so a reader's text search finds it and the
 * text-budget guard (`textBudget.ts`) counts it as body text on the page. Honesty copy "ranked, never
 * deleted" is the same copy, one click away, and for a closed popover or drawer the guard rightly
 * counts nothing because there is nothing there. This is that for an inline disclosure: the same
 * `<details>` and `<summary>` (so the look, the keyboard and the screen reader are the browser's), but
 * the open state is held here and the children are rendered only when it is open.
 *
 * The summary's click is taken over (`preventDefault`) so the state has one owner and a test's
 * `fireEvent.click(summary)` is synchronous. Nothing that holds a form belongs in here: the body is
 * unmounted on close, so anything typed into it would be lost.
 */

import { useState } from "react";
import type { ReactNode } from "react";

export function LazyDetails({
  summary,
  children,
  className,
  summaryClassName,
  testId,
  open: initiallyOpen = false,
}: {
  summary: ReactNode;
  children: ReactNode;
  className?: string;
  summaryClassName?: string;
  testId?: string;
  /** Open on first render (a refusal, say, or a test that reads the words). */
  open?: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <details className={className} data-testid={testId} open={open}>
      <summary
        className={summaryClassName}
        onClick={(event) => {
          event.preventDefault();
          setOpen((now) => !now);
        }}
      >
        {summary}
      </summary>
      {open ? children : null}
    </details>
  );
}
